import { mutate } from 'swr';
import { toast } from 'sonner';
import { useGenerationPolling, type PollingResponseContext, type UseMediaPollingParams } from '@/components/nodes/shared/use-generation-polling';
import { reuseOrAppendHistory } from '@/components/nodes/shared/media-history';
import { notifyAssetLibraryChanged } from '@/components/canvas/utils/asset-folders-events';

export type UseImagePollingParams = UseMediaPollingParams;

function handleResponse(
    s: { status: string; outputUrl?: string; urls?: string[]; size?: string },
    ctx: PollingResponseContext,
): 'break' | 'continue' {
    if (s.status === 'completed' && (s.urls?.length || s.outputUrl)) {
        const nowIso = new Date().toISOString();
        const ld = ctx.getLatestData();
        const urls = Array.isArray(s.urls) && s.urls.length > 0 ? s.urls : (s.outputUrl ? [s.outputUrl] : []);
        const historyType = ld?.generated?.type || 'image/png';
        // 真实尺寸（服务端解析图片字节得到）写入 generated.size + sizeUrl，避免客户端再解析文件头。
        const hasRealSize = typeof s.size === 'string' && /^\d+x\d+$/i.test(s.size);
        const realMeta = hasRealSize ? { size: s.size, sizeUrl: urls[0] } : {};
        const { history, activeHistoryId } = reuseOrAppendHistory(
            ld?.history,
            urls,
            (id) => ({
                id,
                ...(urls.length > 1 ? { urls } : { url: urls[0] }),
                type: historyType,
                description: '',
                createdAt: nowIso,
                prompt: ld?.instructions ?? '',
                modelId: ctx.modelId,
                // 历史条目存“真实输出像素”（绑定该条目 url）；服务端未解析出时回退到设置值。
                size: hasRealSize ? s.size : ld?.size,
            }),
        );
        try {
            ctx.updateNodeData(ctx.nodeId, {
                updatedAt: nowIso,
                status: 'completed',
                model: ctx.modelId,
                generated: { ...ld?.generated, url: urls[0], urls, type: historyType, ...realMeta },
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
                history,
                activeHistoryId,
            });
        } catch {}
        try { toast.success(ctx.t('Image generated successfully')); } catch {}
        notifyAssetLibraryChanged();
        setTimeout(() => mutate(ctx.projectId ? `credits:${ctx.projectId}` : 'credits'), 5000);
        return 'break';
    }

    if (s.status === 'failed') {
        try {
            ctx.updateNodeData(ctx.nodeId, {
                status: 'failed',
                updatedAt: new Date().toISOString(),
                error: 'Image generation failed',
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
            });
        } catch {}
        return 'break';
    }

    return 'continue';
}

export function useImagePolling(params: UseImagePollingParams) {
    useGenerationPolling({
        ...params,
        statusEndpoint: '/api/image/status',
        handleResponse,
    });
}